export interface NormalizedLeadPhone {
  /** e.g. "tel:+923001234567" */
  telHref: string
  /** e.g. "https://wa.me/923001234567" */
  whatsappHref: string
}

export type PakistanPhoneKind = 'mobile' | 'landline'

export interface ParsedPakistanPhone {
  /** E.164 with leading +, no spaces — e.g. "+923001234567" */
  e164: string
  /** National significant number (no 0, no 92) — e.g. "3001234567" */
  nsn: string
  kind: PakistanPhoneKind
}

const COUNTRY_NSN = '92'

function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, '')
}

/**
 * Strip country/trunk prefixes so the user can type 03…, 3…, 021…, 92…, or +92…
 * and we still land on the national number.
 */
export function toPakistanNsn(raw: string): string {
  let digits = digitsOnly(raw)
  if (digits.startsWith(COUNTRY_NSN)) digits = digits.slice(COUNTRY_NSN.length)
  if (digits.startsWith('0')) digits = digits.slice(1)
  return digits
}

function classifyNsn(nsn: string): PakistanPhoneKind | null {
  if (/^3\d{9}$/.test(nsn)) return 'mobile'
  // Geographic: 9–10 digit NSN. 3… is mobile-only; 0/1 are not area codes.
  if (/^[2456789]\d{8,9}$/.test(nsn)) return 'landline'
  return null
}

/** Parse a Pakistani mobile or landline; null when the number cannot be a real PK line. */
export function parsePakistanPhone(raw: string | null | undefined): ParsedPakistanPhone | null {
  if (!raw?.trim()) return null
  const nsn = toPakistanNsn(raw)
  const kind = classifyNsn(nsn)
  if (!kind) return null
  return { e164: `+${COUNTRY_NSN}${nsn}`, nsn, kind }
}

/** E.164 for submit, or null when invalid. */
export function toPakistanPhoneE164(raw: string | null | undefined): string | null {
  return parsePakistanPhone(raw)?.e164 ?? null
}

/** Display grouping: "+92 300 1234567" (mobile) / "+92 21 3456789" (landline). */
export function formatPakistanPhone(raw: string | null | undefined): string | null {
  const parsed = parsePakistanPhone(raw)
  if (!parsed) return raw?.trim() || null
  const { nsn, kind } = parsed
  if (kind === 'mobile') return `+92 ${nsn.slice(0, 3)} ${nsn.slice(3)}`
  if (nsn.length === 9) return `+92 ${nsn.slice(0, 2)} ${nsn.slice(2)}`
  if (nsn.length === 10 && nsn.startsWith('2')) return `+92 ${nsn.slice(0, 2)} ${nsn.slice(2)}`
  return `+92 ${nsn.slice(0, 3)} ${nsn.slice(3)}`
}

/**
 * Normalizes a lead's stored phone number for tel:/wa.me links.
 * Prefers Pakistani E.164; falls back to a generic digit string for legacy rows.
 */
export function normalizeLeadPhone(raw: string | null | undefined): NormalizedLeadPhone | null {
  const parsed = parsePakistanPhone(raw)
  if (parsed) {
    const international = parsed.e164.slice(1)
    return {
      telHref: `tel:${parsed.e164}`,
      whatsappHref: `https://wa.me/${international}`,
    }
  }

  if (!raw) return null

  const hadPlus = raw.trim().startsWith('+')
  const digits = digitsOnly(raw)
  if (!digits) return null

  let canonical = digits
  if (!hadPlus && digits.startsWith('0') && digits.length >= 9 && digits.length <= 11) {
    canonical = `${COUNTRY_NSN}${digits.slice(1)}`
  }

  if (canonical.length < 8 || canonical.length > 15) return null

  return {
    telHref: `tel:+${canonical}`,
    whatsappHref: `https://wa.me/${canonical}`,
  }
}
